import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import DarkModeSwitch from './switches/dark-mode-switch';
import sportjoeLogo from '@/assets/images/SJ_LogoSchriftzug_weiß-transp.svg';
import smallSportjoeLogo from '@/assets/images/SJ_Logo-Ball_weiß-transp.png';
import smallSjOneLogo from '@/assets/images/sj-one-small.png';
import sjOneLogo from '@/assets/images/sjone-logo.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';
import Breadcrumb from './breadcrumb';
import {useDispatch} from 'react-redux';
import {toggleDarkMode} from '@/redux-store/slices/theme';
import {useAppSelector} from '@/redux-store/store';
import {Button as LoginButton} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageSwitch from './switches/language-switch';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import {useCallback, useEffect, useState} from 'react';
import DialogForm from './dialogs/form-dialog';
import UserInfosForm from '@/components/forms/user/infos';
import {useSnackbar} from '@/contexts/snackbar';
import SportsForm from '@/components/forms/user/sports';
import {Company} from '@/types/company';
import ChatMenu from '@/components/matrix-chat/ui/chat-menu';
import {env} from '@/config';
import {getLink} from '@/rest/payments/get-link';
import {purgeAppTag} from '@/redux-store/slices/auth';
import ConfirmationDialog from './dialogs/confirmation-dialog';
import {deleteMe} from '@/rest/user/delete-me';
import {userOwnsPaidEvents} from '@/rest/user/user-owns-paid-events';
import {isHealthManager} from '@/utils';

interface ResponsiveAppBarProps {
  loginButtonOnClick: () => void;
  logoutButtonOnClick: () => void;
  company?: Company;
  firstTranslationKey?: string;
  secondTranslationKey?: string;
  thirdTranslationKey?: string;
  skipBreadcrumb?: number;
  breadcrumbHomePath?: string;
}

const ResponsiveAppBar: React.FC<ResponsiveAppBarProps> = ({
  loginButtonOnClick,
  logoutButtonOnClick,
  company,
  firstTranslationKey,
  secondTranslationKey,
  thirdTranslationKey,
  skipBreadcrumb,
  breadcrumbHomePath
}) => {
  const {showSnackbarMessage} = useSnackbar();

  const [anchorElSettings, setAnchorElSettings] =
    useState<null | HTMLElement>();

  const {
    APP_VARIANT,
    TERMS_OF_USE_URL,
    PRIVACY_POLICY_URL,
    LEGAL_INFORMATION_URL
  } = env;

  const navigate = useNavigate();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>();
  const [anchorElChat, setAnchorElChat] = useState<null | HTMLElement>(null);
  const [userInitials, setUserInitials] = useState('');
  const [showUserInfosForm, setShowUserInfosForm] = useState(false);
  const [showUserSportsForm, setShowUserSportsForm] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);
  const [deleteProfileDialogOpen, setDeleteProfileDialogOpen] = useState(false);
  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const bigLogo = APP_VARIANT === 'sportjoe' ? sportjoeLogo : sjOneLogo;

  const smallLogo =
    APP_VARIANT === 'sportjoe' ? smallSportjoeLogo : smallSjOneLogo;

  const logoSrc = isSmallerScreen ? smallLogo : bigLogo;

  const darkMode = useAppSelector((state) => state.theme.darkMode);
  const userData = useAppSelector((state) => state.userInfos.userInfos);
  const userName = useAppSelector((state) => state.userInfos.userInfos.name);
  const color = useAppSelector((state) => state.userInfos.userInfos.color);
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const userImage = useAppSelector(
    (state) => state.userInfos.userInfos.picture
  );
  const companyLogoFromRedux = useAppSelector(
    (state) => state.company.company.logo
  );
  const companies = useAppSelector(
    (state) => state.userInfos.userInfos.companies || []
  );
  const userLastUpdated = useAppSelector(
    (state) => state.userInfos.userInfos.updatedAt
  );

  const isTrainer = userRole && userRole === 'TRAINER';

  const joinedChats = useAppSelector((state) => state.matrixRooms.roomsJoined);

  const {
    isAuthenticated,
    isPublicCompany,
    keycloakUser,
    stripeAccountVerified
  } = useAppSelector((state) => state.auth);

  const isAdmin = useAppSelector(
    (state) =>
      state.auth.keycloakUser && state.auth.keycloakUser.userRole === 'ADMIN'
  );

  const isUserHealthManager =
    Array.isArray(userData?.companies) &&
    userData.companies.length > 0 &&
    isHealthManager(userData.companies[0].role);

  const userImageAltered = `${userImage}?timestamp=${userLastUpdated}`;

  const companyLogoFromReduxAltered =
    APP_VARIANT === 'sjone' &&
    (companyLogoFromRedux
      ? `${companyLogoFromRedux}?timestamp=${Date.now()}`
      : userData?.companies?.length
      ? `${userData.companies[0].id.logo}?timestamp=${Date.now()}`
      : null);

  const companyLogo =
    companyLogoFromReduxAltered &&
    APP_VARIANT === 'sjone' &&
    !isTrainer &&
    (isAuthenticated || isPublicCompany)
      ? companyLogoFromReduxAltered
      : logoSrc;

  const dispatch = useDispatch();

  const {t} = useTranslation();

  const handleToggleDarkMode = useCallback(() => {
    dispatch(toggleDarkMode());
  }, [dispatch]);

  const handleOpenSettingsMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElSettings(event.currentTarget);
    },
    []
  );

  const handleCloseSettingsMenu = useCallback(() => {
    setAnchorElSettings(null);
  }, []);

  const handleOpenUserMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElUser(event.currentTarget);
    },
    []
  );

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null);
  }, []);

  const handleRedirectToStripeLogin = useCallback(async () => {
    const baseUrl = `${window.location.origin}`;
    const loginLink = await getLink('login_link_standard', baseUrl, baseUrl);
    if (loginLink) {
      window.open(loginLink.url, '_blank');
    } else {
      throw new Error('Failed to retrieve login link');
    }
  }, []);

  const handleUserSettingsClick = useCallback(() => {
    handleCloseUserMenu();
    setShowUserInfosForm(true);
  }, [handleCloseUserMenu]);

  const handleUserSportsClick = useCallback(() => {
    handleCloseUserMenu();
    setShowUserSportsForm(true);
  }, [handleCloseUserMenu]);

  const handleUserSportsUpdateCancel = useCallback(() => {
    setShowUserSportsForm(false);
  }, []);

  const handleUserInfosUpdateCancel = useCallback(() => {
    setShowUserInfosForm(false);
  }, []);

  const onUserInfosUpdated = useCallback(() => {
    setShowUserInfosForm(false);
    showSnackbarMessage(t('user_infos_update_success'), 'success');
  }, [showSnackbarMessage, t]);

  const onUserInfosUpdateError = useCallback(() => {
    showSnackbarMessage(t('user_infos_update_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const onUserSportsUpdated = useCallback(() => {
    setShowUserSportsForm(false);
    showSnackbarMessage(t('interests_saved_success'), 'success');
  }, [showSnackbarMessage, t]);

  const onUserSportsUpdateError = useCallback(() => {
    showSnackbarMessage(t('interests_saved_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const onUserOwnsPaidEvents = useCallback(() => {
    showSnackbarMessage(t('user_owns_paid_events'), 'error');
  }, [showSnackbarMessage, t]);

  const onUserProfileDeleted = useCallback(() => {
    showSnackbarMessage(t('user_deleted'), 'success');
  }, [showSnackbarMessage, t]);

  const handleOpenChatMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElChat(event.currentTarget);
    },
    []
  );

  const handleCloseChatMenu = useCallback(() => {
    setAnchorElChat(null);
  }, []);

  const handleOpenDeleteProfileDialog = useCallback(() => {
    setDeleteProfileDialogOpen(true);
  }, []);

  const handleCancelDeleteProfile = useCallback(() => {
    setDeleteProfileDialogOpen(false);
  }, []);

  const handleDeleteProfile = useCallback(async () => {
    try {
      const response = await userOwnsPaidEvents();

      if (response.ownsPaidEvents) {
        handleCancelDeleteProfile();
        onUserOwnsPaidEvents();
        return;
      }

      await deleteMe();
      logoutButtonOnClick();
      onUserProfileDeleted();
    } catch (error) {
      console.error('Error deleting profile:', error);
    }
  }, [
    handleCancelDeleteProfile,
    logoutButtonOnClick,
    onUserOwnsPaidEvents,
    onUserProfileDeleted
  ]);

  useEffect(() => {
    const nameParts = userName.split(' ');
    let initials;

    if (nameParts.length === 1) {
      initials = nameParts[0].charAt(0).toUpperCase();
    } else {
      initials = nameParts
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
    }

    setUserInitials(initials);
  }, [userName, isAuthenticated, keycloakUser]);

  useEffect(() => {
    if (!keycloakUser) {
      setUserInitials('');
    }
  }, [isAuthenticated, keycloakUser]);

  useEffect(() => {
    if (isAuthenticated && keycloakUser) {
      if (APP_VARIANT === 'sjone') {
        setIsDataLoaded(companies.length !== 0);
      }
      setIsDataLoaded(true);
    }
  }, [APP_VARIANT, companies, isAuthenticated, keycloakUser]);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          p: 1
        }}
      >
        <Container maxWidth={false}>
          {(isDataLoaded || !isAuthenticated) && (
            <Toolbar disableGutters>
              <Breadcrumb
                company={company}
                firstTranslationKey={firstTranslationKey}
                secondTranslationKey={secondTranslationKey}
                thirdTranslationKey={thirdTranslationKey}
                skipBreadcrumb={skipBreadcrumb}
                homePath={breadcrumbHomePath}
              />
              <Link
                to={breadcrumbHomePath || '/'}
                onClick={() => dispatch(purgeAppTag())}
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  cursor: 'pointer'
                }}
              >
                <Tooltip title={t('return_to_homepage')}>
                  <Box
                    component="img"
                    sx={{
                      height: '50px',
                      maxWidth: {xs: '75%', sm: '100%'},
                      maxHeight: {xs: '75%', sm: '100%'},
                      objectFit: 'contain',
                      display: isLogoLoaded ? 'block' : 'none'
                    }}
                    alt={t('company_logo') as string}
                    src={companyLogo || ''}
                    onLoad={() => {
                      setTimeout(() => setIsLogoLoaded(true), 100);
                    }}
                  />
                </Tooltip>
              </Link>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center'
                }}
              >
                <Tooltip title={t('open_user_settings')}>
                  {userInitials ? (
                    <IconButton
                      onClick={handleOpenUserMenu}
                      sx={{
                        marginRight: {xs: 0.5, sm: 1}
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: userImage ? 'transparent' : color,
                          objectFit: 'cover',
                          width: {xs: '30px', sm: '40px'},
                          height: {xs: '30px', sm: '40px'}
                        }}
                      >
                        {userImage ? (
                          <img
                            src={userImageAltered}
                            alt={t('user_avatar')}
                            style={{height: '100%', width: '100%'}}
                          />
                        ) : (
                          userInitials
                        )}
                      </Avatar>
                    </IconButton>
                  ) : (
                    <>
                      <LoginButton
                        variant="contained"
                        onClick={loginButtonOnClick}
                        sx={{
                          display: {xs: 'none', sm: 'flex'},
                          bgcolor: theme.palette.primary.dark,
                          fontWeight: 'bold',
                          marginRight: 2,
                          width: '100px'
                        }}
                      >
                        {t('to_login')}
                      </LoginButton>
                      <IconButton
                        onClick={loginButtonOnClick}
                        sx={{
                          display: {xs: 'flex', sm: 'none'},
                          marginRight: {xs: 0.5, sm: 1}
                        }}
                      >
                        <LoginIcon fontSize="large" />
                      </IconButton>
                    </>
                  )}
                </Tooltip>

                {isAuthenticated && (
                  <ChatMenu
                    chats={joinedChats}
                    anchorEl={anchorElChat}
                    onOpenChatMenu={handleOpenChatMenu}
                    onCloseChatMenu={handleCloseChatMenu}
                  />
                )}

                <Tooltip title={t('open_settings')}>
                  <IconButton onClick={handleOpenSettingsMenu}>
                    <SettingsIcon
                      fontSize="large"
                      sx={{
                        color:
                          theme.palette.customColors?.responsiveAppBarSettings,
                        width: {xs: '30px', sm: '40px'},
                        height: {xs: '30px', sm: '40px'}
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{
                    mt: '45px',
                    width: '300px',
                    '& .MuiMenu-paper': {
                      paddingLeft: '20px'
                    }
                  }}
                  id="settings-menu"
                  anchorEl={anchorElSettings}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  open={Boolean(anchorElSettings)}
                  onClose={handleCloseSettingsMenu}
                >
                  {/* <MenuItem onClick={handleCloseSettingsMenu}> */}
                  <MenuItem
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1
                    }}
                  >
                    <Typography variant="body2">{t('dark_mode')}</Typography>
                    <DarkModeSwitch
                      checked={darkMode}
                      onChange={handleToggleDarkMode}
                      sx={{mr: 4}}
                    />
                  </MenuItem>
                  <MenuItem
                    sx={{
                      mb: 1,
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{marginRight: 8, marginTop: 1}}
                    >
                      {t('language')}
                    </Typography>
                    <LanguageSwitch />
                  </MenuItem>
                  {isAuthenticated && (
                    <MenuItem
                      onClick={handleOpenDeleteProfileDialog}
                      sx={{
                        marginTop: 2,
                        marginBottom: 1,
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography variant="body2" sx={{marginRight: 8}}>
                        {t('delete_profile_dialog_title')}
                      </Typography>
                    </MenuItem>
                  )}
                  <MenuItem
                    sx={{
                      marginTop: 2,
                      marginBottom: 1,
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <a
                      href={TERMS_OF_USE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: 'inherit'
                      }}
                    >
                      <Typography variant="body2" sx={{marginRight: 8}}>
                        {t('terms_of_use')}
                      </Typography>
                    </a>
                  </MenuItem>
                  <MenuItem
                    sx={{
                      marginTop: 2,
                      marginBottom: 1,
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <a
                      href={PRIVACY_POLICY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: 'inherit'
                      }}
                    >
                      <Typography variant="body2" sx={{marginRight: 8}}>
                        {t('privacy_policy')}
                      </Typography>
                    </a>
                  </MenuItem>
                  <MenuItem
                    sx={{
                      marginTop: 2,
                      marginBottom: 1,
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <a
                      href={LEGAL_INFORMATION_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: 'inherit'
                      }}
                    >
                      <Typography variant="body2" sx={{marginRight: 8}}>
                        {t('imprint')}
                      </Typography>
                    </a>
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          )}
        </Container>
      </AppBar>
      <Menu
        id="user-menu"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        sx={{
          '& .MuiPaper-root': {
            width: '200px'
          }
        }}
      >
        {(isUserHealthManager || isAdmin) && APP_VARIANT === 'sjone' && (
          <MenuItem onClick={() => navigate('/admin')}>
            {t('admin_dashboard')}
          </MenuItem>
        )}
        <MenuItem onClick={handleUserSettingsClick}>
          {t('user_settings')}
        </MenuItem>
        {stripeAccountVerified && (
          <MenuItem onClick={handleRedirectToStripeLogin}>
            {t('my_stripe_account')}
          </MenuItem>
        )}
        <MenuItem onClick={handleUserSportsClick}>{t('interests')}</MenuItem>

        <MenuItem
          onClick={() => {
            logoutButtonOnClick();
            handleCloseUserMenu();
          }}
        >
          {t('logout')}
        </MenuItem>
      </Menu>

      <Container
        maxWidth={false}
        sx={
          {
            // bgcolor: theme.palette.background.default,
            // m: 1,
          }
        }
      ></Container>
      <DialogForm
        open={showUserInfosForm}
        onSubmit={onUserInfosUpdated}
        onCancel={handleUserInfosUpdateCancel}
        onError={onUserInfosUpdateError}
        FormComponent={UserInfosForm}
        maxHeightAllowed={'1200px'}
      />
      <DialogForm
        open={showUserSportsForm}
        onSubmit={onUserSportsUpdated}
        onCancel={handleUserSportsUpdateCancel}
        onError={onUserSportsUpdateError}
        FormComponent={SportsForm}
        maxHeightAllowed={'no'}
      />
      <ConfirmationDialog
        open={deleteProfileDialogOpen}
        title={t('delete_profile_dialog_title')}
        message={t('profile_delete_account')}
        onConfirm={handleDeleteProfile}
        onCancel={handleCancelDeleteProfile}
        warn
      />
    </>
  );
};

export default ResponsiveAppBar;
